import { Check, Copy, Terminal } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";
import { Button } from "../primitives/button";
import { Tooltip } from "../primitives/menu";

/** Copies text and reports success for 2 s; the live region announces it. */
export function useCopy(): [boolean, (text: string) => Promise<void>] {
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  React.useEffect(() => () => clearTimeout(timer.current), []);
  const copy = React.useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard access can be denied (insecure context, permissions); the UI still shows the text to copy.
      return;
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  }, []);
  return [copied, copy];
}

export function CopyButton({
  text,
  label = "Copy",
  showLabel = false,
  className,
}: {
  text: string;
  label?: string;
  showLabel?: boolean;
  className?: string;
}) {
  const [copied, copy] = useCopy();
  const Icon = copied ? Check : Copy;
  return (
    <Button
      variant="ghost"
      size={showLabel ? "sm" : "icon"}
      className={cn(showLabel ? "" : "size-7", "nd-above", className)}
      onClick={() => void copy(text)}
      aria-label={showLabel ? undefined : label}
    >
      <Icon aria-hidden />
      {showLabel ? (copied ? "Copied" : label) : null}
      <span role="status" className="sr-only">
        {copied ? "Copied" : ""}
      </span>
    </Button>
  );
}

/** The 32px ghost "Copy CLI" icon button of list and detail headers (ui-spec §3.3). */
export function CopyCliButton({ command }: { command: string }) {
  const [copied, copy] = useCopy();
  return (
    <Tooltip content={copied ? "Copied" : `Copy CLI`}>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Copy CLI command"
        data-copy-cli={command}
        onClick={() => void copy(command)}
      >
        {copied ? <Check aria-hidden /> : <Terminal aria-hidden />}
      </Button>
    </Tooltip>
  );
}
